import { Button, Modal } from 'antd';
import dayjs from 'dayjs';
import type React from 'react';

/**
 * Easter egg: amikor posazita@gmail.com (és csak ő) az ELSŐ Connect+Grow 2026
 * regisztrációt jóváhagyja, ez a hivatalos oklevél popup ugrik fel egyszer.
 * A localStorage `easter:posazita:diploma-shown` flag biztosítja az 1×-séget.
 */

// Akiknek megjelenik az easter egg (Connect+Grow 2026 első approve-jakor).
const ELIGIBLE_EMAILS = new Set<string>(['posazita@gmail.com', 'david@studiopresent.com']);

const CONNECT_GROW_SLUG = 'connect-grow-2026';

const storageKey = (email: string) => `easter:diploma-shown:${email.toLowerCase()}`;

export function shouldShowDiploma(
  userEmail: string | undefined,
  eventSlug: string | undefined
): boolean {
  if (!(userEmail && ELIGIBLE_EMAILS.has(userEmail.toLowerCase()))) return false;
  if (eventSlug && eventSlug !== CONNECT_GROW_SLUG) return false;
  try {
    return localStorage.getItem(storageKey(userEmail)) !== '1';
  } catch {
    return true;
  }
}

export function markDiplomaShown(userEmail: string) {
  try {
    localStorage.setItem(storageKey(userEmail), '1');
  } catch {
    // ignore (private mode)
  }
}

interface Props {
  open: boolean;
  recipientName: string;
  onClose: () => void;
}

export const EasterEggDiploma: React.FC<Props> = ({ open, recipientName, onClose }) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      centered
      destroyOnHidden
      styles={{
        body: { padding: 0 },
        content: { padding: 0, background: 'transparent', boxShadow: 'none' },
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #fefaec 0%, #f5ebcc 100%)',
          border: '10px double #b78b3e',
          padding: '48px 40px 36px',
          textAlign: 'center',
          fontFamily: 'Georgia, "Times New Roman", serif',
          color: '#2a1f0f',
          borderRadius: 8,
          position: 'relative',
          boxShadow: '0 20px 60px -20px rgba(0,0,0,0.4)',
        }}
      >
        {/* Ornaments */}
        <div
          style={{
            fontSize: 28,
            letterSpacing: '0.45em',
            marginBottom: 12,
            color: '#b78b3e',
          }}
        >
          ★ ★ ★
        </div>

        <div
          style={{
            fontSize: 11,
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            color: '#8a6f3a',
            marginBottom: 8,
          }}
        >
          Hivatalos elismerés
        </div>

        <h1
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: 38,
            fontWeight: 700,
            margin: '4px 0 28px',
            letterSpacing: '0.06em',
            color: '#5a3a14',
          }}
        >
          OKLEVÉL
        </h1>

        <div style={{ fontSize: 14, marginBottom: 14, color: '#5a4a30' }}>
          Ezennel hivatalosan tanúsítjuk, hogy
        </div>

        <div
          style={{
            fontFamily:
              '"Snell Roundhand", "Brush Script MT", "Lucida Handwriting", cursive, Georgia',
            fontSize: 44,
            color: '#c0473a',
            margin: '8px 0 12px',
            fontStyle: 'italic',
            lineHeight: 1.1,
          }}
        >
          {recipientName}
        </div>

        <div style={{ fontSize: 14, marginBottom: 18, color: '#5a4a30', lineHeight: 1.7 }}>
          sikeresen elsajátította a következő szakmai képesítést:
        </div>

        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            margin: '8px 24px 14px',
            padding: '18px 16px',
            color: '#2a1f0f',
            borderTop: '1px solid #b78b3e',
            borderBottom: '1px solid #b78b3e',
            letterSpacing: '0.02em',
            lineHeight: 1.25,
          }}
        >
          „Konferenciajegy visszaigazoló
          <br />
          asszisztens technikus"
        </div>

        <div style={{ fontSize: 12, color: '#7a6850', margin: '0 24px 24px', fontStyle: 'italic' }}>
          adományozza:&nbsp;easyevnts &amp; Studio Present
        </div>

        <div
          style={{
            marginTop: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            fontSize: 11,
            color: '#5a4a30',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            gap: 24,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: '"Brush Script MT", "Snell Roundhand", cursive',
                fontSize: 24,
                color: '#5a3a14',
                fontStyle: 'italic',
                textTransform: 'none',
                letterSpacing: 0,
                marginBottom: 2,
              }}
            >
              David
            </div>
            <div style={{ borderTop: '1px solid #b78b3e', paddingTop: 4 }}>
              Esemény hivatalos jóváhagyója
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 18,
                color: '#5a3a14',
                textTransform: 'none',
                letterSpacing: 0,
                marginBottom: 2,
              }}
            >
              Connect+Grow 2026
            </div>
            <div style={{ borderTop: '1px solid #b78b3e', paddingTop: 4 }}>
              Kiállítva: {dayjs().format('YYYY. MM. DD.')}
            </div>
          </div>
        </div>

        <div style={{ fontSize: 44, marginTop: 26, letterSpacing: '0.35em' }}>🎖️ 🎫 🎖️</div>

        <Button
          type="primary"
          size="large"
          onClick={onClose}
          style={{ marginTop: 24, minWidth: 240 }}
        >
          Köszönöm, ez nagyon megható!
        </Button>
      </div>
    </Modal>
  );
};
